/** @format **/

/**
 * External dependencies
 */
import React, { useCallback, useEffect, useState } from 'react';
import { __, _n, sprintf } from '@wordpress/i18n';
import { TableCard, TableCardColumn } from '@woocommerce/components';
import { onQueryChange, getQuery } from '@woocommerce/navigation';
import { Button } from '@wordpress/components';

/**
 * Internal dependencies
 */
import { displayType } from 'documents/strings';
import {
	Document,
	useDocuments,
	useDocumentsSummary,
} from 'wcpay/data/documents';
import './style.scss';
import DocumentsFilters from '../filters';
import Page from '../../components/page';
import { getDocumentUrl } from 'wcpay/utils';
import VatFormModal from 'wcpay/vat/form-modal';
import { formatDateTimeFromString } from 'wcpay/utils/date-time';
import { usePersistedColumnVisibility } from 'wcpay/hooks/use-persisted-table-column-visibility';

interface Column extends TableCardColumn {
	key: 'date' | 'type' | 'description' | 'download';
	visible?: boolean;
	cellClassName?: string;
}

const getColumns = (): Column[] =>
	[
		{
			key: 'date',
			label: __( 'Date', 'woocommerce-payments' ),
			screenReaderLabel: __( 'Date and time', 'woocommerce-payments' ),
			required: true,
			isLeftAligned: true,
			defaultOrder: 'desc',
			cellClassName: 'date',
			isSortable: true,
			defaultSort: true,
		},
		{
			key: 'type',
			label: __( 'Type', 'woocommerce-payments' ),
			screenReaderLabel: __( 'Type', 'woocommerce-payments' ),
			required: true,
			isLeftAligned: true,
		},
		{
			key: 'description',
			label: __( 'Description', 'woocommerce-payments' ),
			screenReaderLabel: __( 'Description', 'woocommerce-payments' ),
		},
		{
			key: 'download',
			label: __( 'Download', 'woocommerce-payments' ),
			screenReaderLabel: __( 'Download', 'woocommerce-payments' ),
			isLeftAligned: false,
			isNumeric: true,
		},
	].filter( Boolean ) as Column[]; // We explicitly define the type because TypeScript can't infer the type post-filtering.

const getDocumentDescription = ( document: Document ) => {
	switch ( document.type ) {
		case 'vat_invoice':
			if ( document.period_from && document.period_to ) {
				return sprintf(
					__( 'Tax invoice for %s to %s', 'woocommerce-payments' ),
					formatDateTimeFromString( document.period_from ),
					formatDateTimeFromString( document.period_to )
				);
			}
			return __(
				'Tax invoice without proper period dates',
				'woocommerce-payments'
			);

		default:
			return __( 'Unknown document type', 'woocommerce-payments' );
	}
};

type InterruptedDownload = {
	documentId: Document[ 'document_id' ];
	type: Document[ 'type' ];
	newTab: boolean;
} | null;

const getInitialInterruptedDownload = (): InterruptedDownload => {
	const { document_id: documentId, document_type: documentType } =
		getQuery() as {
			document_id?: Document[ 'document_id' ];
			document_type?: Document[ 'type' ];
		};
	if ( ! documentId || ! documentType ) {
		return null;
	}
	return { documentId, type: documentType, newTab: false };
};

const isBlockedByVat = ( type: Document[ 'type' ] ): boolean =>
	type === 'vat_invoice' && ! wcpaySettings.accountStatus.hasSubmittedVatData;

export const DocumentsList = (): JSX.Element => {
	const { documents, isLoading } = useDocuments( getQuery() );
	const { documentsSummary, isLoading: isSummaryLoading } =
		useDocumentsSummary( getQuery() );

	// Seed both bits of state from the initial URL query so the effect below
	// only needs to open the download window (a legitimate external-system
	// side effect), rather than synchronously calling setState from inside it.
	const [ isVatFormModalOpen, setVatFormModalOpen ] = useState< boolean >(
		() => {
			const initial = getInitialInterruptedDownload();
			return initial !== null && isBlockedByVat( initial.type );
		}
	);

	const [ interruptedDownloadDocument, setInterruptedDownloadDocument ] =
		useState< InterruptedDownload >( getInitialInterruptedDownload );

	const handleDocumentDownload = (
		documentId: Document[ 'document_id' ],
		type: Document[ 'type' ],
		newTab: boolean
	): boolean => {
		setInterruptedDownloadDocument( { documentId, type, newTab } );

		if ( isBlockedByVat( type ) ) {
			setVatFormModalOpen( true );
			return false;
		}

		return true;
	};

	const downloadDocument = useCallback(
		(
			documentId: Document[ 'document_id' ],
			type: Document[ 'type' ],
			newTab = true
		) => {
			const url = getDocumentUrl( documentId );
			if ( handleDocumentDownload( documentId, type, newTab ) ) {
				window.open( url, newTab ? '_blank' : '_self' );
			}
		},
		[]
	);

	const onVatFormCompleted = () => {
		setVatFormModalOpen( false );
		// Set the flag to true so that the user can download the document without refreshing the page.
		wcpaySettings.accountStatus.hasSubmittedVatData = true;
		// Attempt to download the previous document, once the VAT details have been submitted.
		if ( interruptedDownloadDocument ) {
			downloadDocument(
				interruptedDownloadDocument.documentId,
				interruptedDownloadDocument.type,
				interruptedDownloadDocument.newTab
			);
		}
	};

	// Check if the page view is requesting a specific document and trigger its download.
	const {
		document_id: requestedDocumentID,
		document_type: requestedDocumentType,
	} = getQuery();

	useEffect( () => {
		if ( ! requestedDocumentID || ! requestedDocumentType ) {
			return;
		}
		const type = requestedDocumentType as Document[ 'type' ];
		if ( isBlockedByVat( type ) ) {
			// Modal state is already seeded from the URL by the initializer
			// above; nothing else to do until the VAT form is completed.
			return;
		}
		window.open( getDocumentUrl( requestedDocumentID ), '_self' );
	}, [ requestedDocumentID, requestedDocumentType ] );

	const columns = getColumns();

	const { columnsToDisplay, onColumnsChange } =
		usePersistedColumnVisibility< Column >(
			'wc_payments_documents_hidden_columns',
			columns
		);

	const totalRows = documentsSummary.count || 0;
	const rows = documents.map( ( document: Document ) => {
		const documentType =
			displayType[ document.type ] ??
			__( 'Unknown document type', 'woocommerce-payments' );
		// Map document into table row.
		const data = {
			date: {
				value: document.date,
				display: formatDateTimeFromString( document.date ),
			},
			type: {
				value: documentType,
				display: documentType,
			},
			description: {
				value: getDocumentDescription( document ),
				display: getDocumentDescription( document ),
			},
			download: {
				value: getDocumentUrl( document.document_id ),
				display: (
					<Button
						className="documents-list__download-button"
						variant="link"
						onClick={ () =>
							downloadDocument(
								document.document_id,
								document.type
							)
						}
						__next40pxDefaultSize
					>
						{ __( 'Download', 'woocommerce-payments' ) }
					</Button>
				),
			},
		};

		return columnsToDisplay.map(
			( { key } ) => data[ key ] || { display: null }
		);
	} );

	const title = __( 'Documents', 'woocommerce-payments' );

	// initializing summary with undefined as we don't want to render the TableSummary component unless we have the data
	let summary;
	const isDocumentsSummaryDataLoaded =
		documentsSummary.count !== undefined && isSummaryLoading === false;

	// Generate summary only if the data has been loaded
	if ( isDocumentsSummaryDataLoaded ) {
		summary = [
			{
				label: _n(
					'document',
					'documents',
					// We've already checked that `.count` is not undefined, but TypeScript doesn't detect
					// that so we remove the `undefined` in the type manually.
					documentsSummary.count as number,
					'woocommerce-payments'
				),
				value: `${ documentsSummary.count }`,
			},
		];
	}

	return (
		<Page>
			<DocumentsFilters />
			<TableCard
				className="documents-list woocommerce-report-table"
				title={ title }
				isLoading={ isLoading }
				rowsPerPage={ parseInt( getQuery().per_page ?? '', 10 ) || 25 }
				totalRows={ totalRows }
				headers={ columnsToDisplay }
				rows={ rows }
				summary={ summary }
				query={ getQuery() }
				onQueryChange={ onQueryChange }
				onColumnsChange={ onColumnsChange }
				actions={ [] }
			/>
			<VatFormModal
				isModalOpen={ isVatFormModalOpen }
				setModalOpen={ setVatFormModalOpen }
				onCompleted={ onVatFormCompleted }
			/>
		</Page>
	);
};

export default DocumentsList;
